// Desktop-specific constants and styling

export const DESKTOP_CONSTANTS = {
  // Layout dimensions
  SIDEBAR_WIDTH: 280,
  HEADER_HEIGHT: 80,
  CONTENT_PADDING: 24,
  
  // Table dimensions
  TABLE_ROW_HEIGHT: 56,
  TABLE_HEADER_HEIGHT: 48,
  TABLE_MIN_COLUMN_WIDTH: 120,
  TABLE_MAX_WIDTH: '100%',
  
  // Breakpoints for desktop-specific behavior
  BREAKPOINTS: {
    SHOW_ALL_COLUMNS: 1280,
    SHOW_SIDEBAR: 1024,
    COMPACT_MODE: 1440,
    // Below this width, switch master-detail layouts (e.g. Attendance tab)
    // from a side-by-side split to a single column with a modal overlay.
    SPLIT_DETAILS: 1280,
  },
  
  // Animation durations
  TRANSITIONS: {
    SIDEBAR_TOGGLE: 300,
    TABLE_SORT: 200,
    HOVER_EFFECT: 150,
  },
} as const;

export const DESKTOP_COLORS = {
  // Desktop-specific color overrides
  TABLE: {
    // Match admin-web DataGrid dark styling
    // Backgrounds
    // BACKGROUND: '#121212',
    // HEADER_BACKGROUND: '#171717',
    BACKGROUND: '#171717',
    HEADER_BACKGROUND: '#121212',
    // Borders (header bottom: #5E5E5E, cell/row: #464646)
    BORDER: '#464646',
    ROW_HOVER: '#1A1A1A',
    ROW_SELECTED: '#1E1E1E',
    // Text
    TEXT: '#EEF1F4',
    TEXT_SECONDARY: '#919191',
    // Icons (sort/menu/separators)
    SORT_INDICATOR: '#919191',
  },
  
  SIDEBAR: {
    // Keep sidebar subtle dark to blend with table on desktop
    BACKGROUND: '#121212',
    BORDER: '#2A2A2A',
    ITEM_HOVER: '#1A1A1A',
    ITEM_ACTIVE: '#1E1E1E',
    TEXT: '#EEF1F4',
    TEXT_SECONDARY: '#919191',
    ACTIVE_INDICATOR: '#2196F3',
  },
  
  HEADER: {
    BACKGROUND: '#121212',
    BORDER: '#2A2A2A',
    TEXT: '#EEF1F4',
    TEXT_SECONDARY: '#919191',
  },
  
  BUTTON: {
    PRIMARY_BACKGROUND: '#2196F3',
    PRIMARY_TEXT: '#FFFFFF',
    PRIMARY_HOVER: '#1976D2',
    SECONDARY_BACKGROUND: 'transparent',
    SECONDARY_TEXT: '#919191',
    SECONDARY_BORDER: '#464646',
    SECONDARY_HOVER: '#1A1A1A',
  },
} as const;

// Desktop-specific style mixins
export const DESKTOP_STYLES = {
  // Card styling for desktop
  card: {
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.TABLE.BORDER,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  
  // Table styling
  table: {
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.TABLE.BORDER,
    overflow: 'hidden',
  },
  
  tableHeader: {
    backgroundColor: DESKTOP_COLORS.TABLE.HEADER_BACKGROUND,
    borderBottomWidth: 1,
    borderBottomColor: DESKTOP_COLORS.TABLE.BORDER,
    height: DESKTOP_CONSTANTS.TABLE_HEADER_HEIGHT,
  },
  
  tableRow: {
    height: DESKTOP_CONSTANTS.TABLE_ROW_HEIGHT,
    borderBottomWidth: 1,
    borderBottomColor: DESKTOP_COLORS.TABLE.BORDER,
  },
  
  // Button styling
  primaryButton: {
    backgroundColor: DESKTOP_COLORS.BUTTON.PRIMARY_BACKGROUND,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  
  secondaryButton: {
    backgroundColor: DESKTOP_COLORS.BUTTON.SECONDARY_BACKGROUND,
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.BUTTON.SECONDARY_BORDER,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  
  // Text styling
  primaryText: {
    color: DESKTOP_COLORS.TABLE.TEXT,
    fontSize: 14,
  },
  
  secondaryText: {
    color: DESKTOP_COLORS.TABLE.TEXT_SECONDARY,
    fontSize: 12,
  },
  
  headerText: {
    color: DESKTOP_COLORS.HEADER.TEXT,
    fontSize: 24,
    fontWeight: '600',
  },
} as const;

// Utility function to get responsive styles
export const getResponsiveStyles = (isDesktop: boolean) => {
  return isDesktop ? DESKTOP_STYLES : {};
};

// Desktop-style confirmation copy to reuse on mobile for parity
export const DESKTOP_CONFIRM_COPY = {
  discardTitle: 'Discard Changes',
  discardMessage: 'Are you sure you want to discard your changes?',
  discardConfirmText: 'Discard',
  discardCancelText: 'Keep Editing',
  // discardSecondMessage:
  //   'Are you absolutely sure you want to discard your changes? This action cannot be undone.',
  signOut: {
    title: 'Sign Out',
    message: 'Are you sure you want to sign out? You will need to log in again to access the admin panel.',
    confirmText: 'Sign Out',
    cancelText: 'Cancel',
  },
  deleteUser: {
    title: 'Delete User?',
    message: 'Are you sure you want to delete this user? This will revoke their roles and remove their account.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    finalTitle: 'Are you absolutely sure?',
    finalMessage: 'This action cannot be undone. The user account and all associated role assignments will be permanently deleted.',
    finalConfirmText: 'Yes, Delete Permanently',
    finalCancelText: 'Cancel',
  },
} as const;
